import { COLOR_OR_TOKEN_PATTERN_WITH_RGBA, enhanceOptionsSchemaWithDynamicOptions } from '../utils';

export const processTreeOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description: 'Specify the color for the background.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    source: {
        default: '> primary | frameBySeriesTypes("string") | seriesByIndex(0)',
        description: 'Specify the source node id for each link row.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    target: {
        default: '> primary | frameBySeriesTypes("string") | seriesByIndex(1)',
        description: 'Specify the target node id for each link row.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeId: {
        default: '> nodeSource | seriesByName("node_id")',
        description: 'Specify node ids for node metadata lookup when using a secondary node datasource.',
        type: 'array',
        items: {
            type: 'string',
        },
    },
    nodeColors: {
        default: [],
        description:
            'Specify optional node row background colors aligned with nodeSource rows. These colors can fill process tree rows and right-side network rows unless the node is highlighted. Use networkNodeColors to color network indicators. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    networkNodeColors: {
        default: [],
        description:
            'Specify optional network node indicator colors aligned with nodeSource rows. These colors apply only to right-side network nodes and the process-side circle indicators for those networks. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    nodeIcons: {
        default: [],
        description:
            'Specify optional node icons aligned with nodeSource rows. Supported built-in values are fire, rectangleDashed, cube, servers, and container. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    linkValues: {
        default: [],
        description: 'Specify link values for each link row.',
        type: 'array',
        items: {
            type: ['number', 'string'],
        },
    },
    highlightedNodeId: {
        default: '',
        description: 'Specify a node id to highlight in the process tree.',
        type: 'string',
    },
    highlightColor: {
        default: 'rgba(129, 51, 74, 0.82)',
        description: 'Specify the background color used for the highlighted node row.',
        type: 'string',
    },
    highlightIcon: {
        default: '',
        description:
            'Specify an optional built-in icon to render for the highlighted node. Supported values are fire, rectangleDashed, cube, servers, and container.',
        type: 'string',
    },
    highlightIconColor: {
        default: '',
        description: 'Specify an optional color override for the highlighted node icon.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    treeNodeLabelFields: {
        default: [],
        description:
            'Specify nodeSource field names to render in each tree node label, joined by spaces. Array values render as comma-separated text. Empty or null values are skipped. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    fieldsToShowAsChip: {
        default: [],
        description:
            'Specify treeNodeLabelFields names whose values should be rendered as chips. Array values render as comma-separated text inside a single chip. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    showFieldName: {
        default: false,
        description:
            'Specify whether tree node labels render as field=value. When false, only the field value is shown.',
        type: 'boolean',
    },
    disableExpansionPerNode: {
        default: [],
        description:
            "Specify per-node expansion behavior aligned with nodeSource rows. Use 'disable' to prevent collapsing/expanding that node; 'enable' or null allows expansion. Provide an array, or use a string DSL expression that resolves to an array.",
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    filteredNodeField: {
        description:
            'Specify node field name used to classify which nodes render in the right panel and are excluded from the left tree.',
        type: 'string',
    },
    filteredNodeValues: {
        description:
            'Specify field value(s) used with filteredNodeField to classify which nodes render in the right panel. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    filterButtonFields: {
        default: [],
        description:
            'Specify nodeSource field names used by connection filter buttons. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    filterButtonMatchValues: {
        default: [],
        description:
            'Specify match values used by connection filter buttons. Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    filterButtonNames: {
        default: [],
        description:
            'Specify display names for connection filter buttons. Provide an array, or use a string DSL expression that resolves to an array. Falls back to filterButtonMatchValues when omitted.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
    filterButtonColors: {
        default: [],
        description:
            'Specify optional colors for connection filter buttons (for example #FF0000). Provide an array, or use a string DSL expression that resolves to an array.',
        type: ['array', 'string'],
        items: {
            type: 'string',
        },
    },
});
